'use client';

import Image from 'next/image';
import { cn } from '@/lib/utils';

interface ProviderLogoConfig {
  src: string;
  label: string;
  /**
   * Set when the artwork is single-color and would vanish against one theme:
   * 'dark' means the asset is drawn in dark ink (invisible on the dark theme),
   * 'light' means it is drawn in light ink (invisible on the light theme).
   * The renderer inverts the image on the theme where it would disappear, so
   * every surface that shows provider logos stays legible in both themes.
   */
  monochromeInk?: 'dark' | 'light';
}

interface ProviderLogoProps {
  providerId: string | null | undefined;
  className?: string;
}

const PROVIDER_LOGOS: Record<string, ProviderLogoConfig> = {
  openai: {
    src: '/provider-logos/openai.svg',
    label: 'OpenAI',
    monochromeInk: 'light',
  },
  prov_openai: {
    src: '/provider-logos/openai.svg',
    label: 'OpenAI',
    monochromeInk: 'light',
  },
  // Gemini deliberately renders the Google logo: the product ships under
  // Google's brand and the colored mark stays visible on both themes.
  gemini: {
    src: '/provider-logos/google.svg',
    label: 'Gemini',
  },
  google: {
    src: '/provider-logos/google.svg',
    label: 'Google',
  },
  github: {
    src: '/provider-logos/github.svg',
    label: 'GitHub',
    monochromeInk: 'dark',
  },
  azure_devops: {
    src: '/provider-logos/azure-devops.svg',
    label: 'Azure DevOps',
  },
  lua: {
    src: '/provider-logos/lua.png',
    label: 'LUA',
    monochromeInk: 'dark',
  },
  microsoft: {
    src: '/provider-logos/microsoft.svg',
    label: 'Microsoft',
  },
  // Moonitor-hosted Standard tools (Time, Content Editor, ...) carry our own
  // provider id, so they sit next to Google, Microsoft, and GitHub in the
  // toolbox with the same treatment.
  moonitor: {
    src: '/provider-logos/moonitor.svg',
    label: 'Moonitor',
  },
  meta: {
    src: '/provider-logos/meta.svg',
    label: 'Meta',
  },
  qwen: {
    src: '/provider-logos/qwen.svg',
    label: 'Qwen',
  },
  xai: {
    src: '/provider-logos/xai.svg',
    label: 'Grok (xAI)',
    monochromeInk: 'dark',
  },
  moonshot: {
    src: '/provider-logos/moonshot.svg',
    label: 'Kimi (Moonshot AI)',
    monochromeInk: 'dark',
  },
  anthropic: {
    src: '/provider-logos/anthropic.svg',
    label: 'Anthropic',
    monochromeInk: 'dark',
  },
  atlassian: {
    src: '/provider-logos/atlassian.svg',
    label: 'Atlassian',
  },
  slack: {
    src: '/provider-logos/slack.svg',
    label: 'Slack',
  },
  // Both Teams providers (organization consent and personal sign-in) are the
  // same product to the reader, so they share the Teams mark rather than the
  // generic Microsoft one.
  microsoft_teams: {
    src: '/provider-logos/microsoft-teams.svg',
    label: 'Microsoft Teams',
  },
  microsoft_teams_user: {
    src: '/provider-logos/microsoft-teams.svg',
    label: 'Microsoft Teams',
  },
  huggy: {
    src: '/provider-logos/huggy.svg',
    label: 'Huggy',
  },
  twilio: {
    src: '/provider-logos/twilio.svg',
    label: 'Twilio',
  },
  movingpay: {
    src: '/provider-logos/movingpay.svg',
    label: 'MovingPay',
  },
  bigdatacorp: {
    src: '/provider-logos/bigdatacorp.svg',
    label: 'BigDataCorp',
  },
  meta_ads: {
    src: '/provider-logos/meta.svg',
    label: 'Meta Ads',
  },
  servicenow: {
    src: '/provider-logos/servicenow.svg',
    label: 'ServiceNow',
  },
  clickup: {
    src: '/provider-logos/clickup.svg',
    label: 'ClickUp',
  },
};

/**
 * Theme-side inversion for single-color artwork. Dark-ink assets flip on the
 * dark theme; light-ink assets flip on the light theme. Colored logos need
 * no treatment and get no class.
 */
const MONOCHROME_INK_CLASSES: Record<NonNullable<ProviderLogoConfig['monochromeInk']>, string> = {
  dark: 'dark:invert',
  light: 'invert dark:invert-0',
};

function normalizeProviderId(providerId: string | null | undefined): string {
  return (providerId ?? '').trim().toLowerCase();
}

export function getProviderLogo(providerId: string | null | undefined): ProviderLogoConfig | null {
  return PROVIDER_LOGOS[normalizeProviderId(providerId)] ?? null;
}

export function ProviderLogo({ providerId, className }: ProviderLogoProps) {
  const providerLogo = getProviderLogo(providerId);

  if (!providerLogo) {
    return null;
  }

  return (
    <span className={cn('relative block shrink-0', className)} title={providerLogo.label}>
      {/* Eager on purpose: a logo is a few hundred bytes of SVG, and lazy
          loading left most of them unrequested on long pages such as the
          catalog, where they only appeared after scrolling past them twice. */}
      <Image
        src={providerLogo.src}
        alt=""
        fill
        sizes="20px"
        loading="eager"
        className={cn(
          'object-contain',
          providerLogo.monochromeInk && MONOCHROME_INK_CLASSES[providerLogo.monochromeInk],
        )}
        aria-hidden="true"
      />
    </span>
  );
}
